import { getTheme, IProgressIndicatorProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPProgressIndicator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesCard } from 'globalStyles';
import { useContext } from 'react';
import { IPSharedColors } from 'themes/runtime/fluentColors';

interface IChecklistProgressProps {
  compact?: boolean;
  totalChecks: number;
  completedChecks?: number;
  successChecks?: number;
  failedChecks?: number;
}

const ChecklistProgress = (props: IChecklistProgressProps) => {
  const appContext = useContext(AppContext);
  const theme = getTheme();

  let completedChecks = props.completedChecks;
  if (completedChecks === undefined) {
    completedChecks = (props.successChecks ?? 0) + (props.failedChecks ?? 0);
  }

  const onRenderProgress = (
    progressProps?: IProgressIndicatorProps,
    defaultRender?: (props?: IProgressIndicatorProps) => JSX.Element | null,
  ) => {
    const total = props.totalChecks > 0 ? props.totalChecks : 1;

    const successPercent = (props.successChecks ?? 0) / total;
    const failedPercent = (props.failedChecks ?? 0) / total;
    const remainingPercent = Math.max(0, 1 - successPercent - failedPercent);

    const barHeight = progressProps?.barHeight ?? 8;

    const segmentBaseStyle: React.CSSProperties = {
      height: '100%',
      transition: 'width 0.3s ease', // smooth animation
    };

    return (
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: barHeight,
          borderRadius: barHeight / 2,
          overflow: 'hidden',
        }}
      >
        {/* Success */}
        <div
          style={{
            ...segmentBaseStyle,
            width: `${successPercent * 100}%`,
            backgroundColor: IPSharedColors.green20,
          }}
        />

        {/* Failed */}
        <div
          style={{
            ...segmentBaseStyle,
            width: `${failedPercent * 100}%`,
            backgroundColor: IPSharedColors.red20,
          }}
        />

        {/* Remaining */}
        <div
          style={{
            ...segmentBaseStyle,
            width: `${remainingPercent * 100}%`,
            backgroundColor: appContext.useDarkMode ? theme.palette.neutralPrimary : theme.palette.neutralLight,
          }}
        />
      </div>
    );
  };

  //
  // Main render
  //
  return (
    <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign={'center'} grow>
      <IPStack.Item>
        <IPText block variant={props.compact ? 'small' : 'medium'} styles={globalTextStylesCard}>
          {`${completedChecks}/${props.totalChecks}`}
        </IPText>
      </IPStack.Item>
      <IPStack.Item grow>
        <IPProgressIndicator
          barHeight={props.compact ? 6 : 10}
          percentComplete={props.totalChecks > 0 ? completedChecks / props.totalChecks : 0}
          onRenderProgress={props.completedChecks === undefined ? onRenderProgress : undefined}
        />
      </IPStack.Item>
      <IPStack.Item>
        {props.totalChecks > 0 && props.totalChecks === completedChecks ? (
          <IPFontIcon
            iconName="SkypeCircleCheck"
            style={{ fontSize: `${props.compact ? '12px' : '20px'}`, color: IPSharedColors.green20 }}
          />
        ) : (
          <IPFontIcon iconName="CircleRing" style={{ fontSize: `${props.compact ? '12px' : '20px'}` }} />
        )}
      </IPStack.Item>
    </IPStack>
  );
};

export default ChecklistProgress;
